import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Add custom styles for markers
const style = document.createElement('style');
style.textContent = `
  .service-marker {
    transition: all 0.3s ease;
    stroke: #fff;
    stroke-width: 2px;
    cursor: pointer;
  }
  .service-marker:hover {
    stroke-width: 3px;
    stroke-opacity: 1;
    filter: drop-shadow(0 0 5px rgba(0,0,0,0.3));
  }
  .status-2 { /* En servicio */
    fill: #28a745 !important;
  }
  .status-1 { /* Asignado */
    fill: #ffc107 !important;
  }
  .status-3 { /* Cancelado */
    fill: #dc3545 !important;
  }
  .leaflet-popup-content {
    margin: 10px !important;
    min-width: 220px;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 8px !important;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important;
  }
  .service-popup {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }
  .service-popup h4 {
    margin: 0 0 10px 0;
    color: #2c3e50;
    font-size: 16px;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
  }
  .service-popup p {
    margin: 5px 0;
    font-size: 14px;
    color: #555;
  }
  .service-popup .badge {
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
  }
  }
`;
document.head.appendChild(style);

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const ActiveServicesMap = ({ mapadata = [] }) => {
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  // Helper function to get status text
  const getStatusText = (status) => {
    switch(status) {
      case 2: return 'En Servicio';
      case 1: return 'Asignado';
      case 3: return 'Cancelado';
      default: return 'Desconocido';
    }
  };

  // Format phone number
  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : phone;
  };

  // Process map data
  const processMapData = (data) => {
    if (!data) {
      console.warn('No data provided to processMapData');
      return [];
    }
    
    console.log('Raw data received:', data);
    
    // Handle different data structures
    let rawData = [];
    if (Array.isArray(data)) {
      rawData = data;
    } else if (data && Array.isArray(data.data)) {
      rawData = data.data;
    } else if (data && data.activos && Array.isArray(data.activos)) {
      rawData = data.activos;
    } else if (typeof data === 'object' && !Array.isArray(data)) {
      // If it's an object but not an array, try to extract locations
      rawData = Object.values(data).filter(item => 
        item && (item.lat !== undefined || item.latitud !== undefined)
      );
    }
    
    console.log('Processing map data, raw data count:', rawData.length);
    
    // Process and validate each item
    const processed = rawData.map((item, index) => {
      if (!item) return null;
      
      // Handle different coordinate field names
      const lat = parseFloat(item.latitud || item.lat || item.latitude || 0);
      const lng = parseFloat(item.longitud || item.lng || item.long || item.longitude || 0);
      
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(`Skipping service ${index} - Invalid coordinates:`, { lat, lng, item });
        return null;
      }
      
      // Ensure we have valid coordinates within reasonable bounds
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn(`Skipping service ${index} - Coordinates out of bounds:`, { lat, lng });
        return null;
      }
      
      // Ensure coordinates are not 0,0 (default/fallback value)
      if (lat === 0 && lng === 0) {
        console.warn(`Skipping service ${index} - Default coordinates (0,0) detected`);
        return null;
      }
      
      return {
        ...item,
        lat,
        lng,
        // Add fallback for required fields
        nombre_cliente: item.nombre_cliente || 'Cliente',
        estado_servicio: item.estado_servicio || 0,
        telefono: item.telefono || item.numero_contacto || '',
        direccion_inicial: item.direccion_inicial || 'Sin dirección',
        vehiculo_placa: item.vehiculo_placa || item.placa || ''
      };
    }).filter(Boolean); // Remove any null entries
    
    console.log(`Processed ${processed.length} valid locations out of ${rawData.length} total`);
    return processed;
  };

  const serviceLocations = processMapData(mapadata);
  console.log('Processed service locations:', serviceLocations);

  // Initialize map and handle markers
  useEffect(() => {
    // Initialize map if not already done
    if (mapRef.current && !mapInstance) {
      const map = L.map(mapRef.current, {
        zoomControl: false,
        maxZoom: 18,
        minZoom: 5
      }).setView([4.7109, -74.0721], 8);
      
      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);
      
      // Add zoom control with better position
      L.control.zoom({
        position: 'topright'
      }).addTo(map);
      
      setMapInstance(map);
      return;
    }
    
    // Skip if no map instance
    if (!mapInstance) return;
    
    // Clear existing markers and controls
    mapInstance.eachLayer((layer) => {
      if (layer instanceof L.Marker || 
          layer instanceof L.CircleMarker || 
          layer instanceof L.Control) {
        mapInstance.removeLayer(layer);
      }
    });
    
    // Process the map data
    const locations = processMapData(mapadata);
    const groupedByLocation = locations.reduce((acc, service) => {
      const key = `${service.lat},${service.lng}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(service);
      return acc;
    }, {});
    console.log('Locations to display:', locations);
    
    // If no valid locations, show message
    if (locations.length === 0) {
      const message = L.control({position: 'topright'});
      message.onAdd = function() {
        const div = L.DomUtil.create('div', 'info legend');
        // div.innerHTML = `
        //   <div class="bg-white p-3 rounded shadow-sm" style="max-width: 250px;">
        //     <div class="text-center">
        //       <i class="fas fa-map-marker-alt fa-2x text-muted mb-2"></i>
        //       <p class="mb-0 fw-bold">No se encontraron servicios</p>
        //       <small class="text-muted">Verifica los filtros o la conexión</small>
        //     </div>
        //   </div>`;
        return div;
      };
      message.addTo(mapInstance);
      mapInstance.setView([4.5709, -74.2973], 6); // Default to Colombia view
      // return;
    }
    else{
      // eliminar el de arriba si existe
      mapInstance.eachLayer((layer) => {
        if (layer instanceof L.Control) {
          mapInstance.removeLayer(layer);
        }
      });
      // si llego a existir el verifica los filtros o la conexion y mostrar un resumen con la informacion
      const message = L.control({position: 'topright'});
      message.onAdd = function() {
        const div = L.DomUtil.create('div', 'info legend');
        // div.innerHTML = `
        //   <div class="bg-white p-3 rounded shadow-sm" style="max-width: 250px;">
        //     <div class="text-center">
        //       <i class="fas fa-map-marker-alt fa-2x text-muted mb-2"></i>
        //       <p class="mb-0 fw-bold">Resumen de servicios</p>
        //       <p class="mb-0 fw-bold">Total de servicios: ${locations.length}</p>
        //     </div>
        //   </div>`;
        return div;
      };
      message.addTo(mapInstance);
      // mapInstance.setView([4.5709, -74.2973], 6); // Default to Colombia view
      // return;
    }

    // Create a feature group to store all markers
    const markers = L.featureGroup();
    const bounds = [];
    
    console.log('Creating markers for locations:', locations);
    
    // Add markers for each location
    Object.values(groupedByLocation).forEach((servicesAtLocation) => {
      const { lat, lng } = servicesAtLocation[0]; // todos tienen mismas coords
    
      // 👀 aquí puedes decidir el color del marker (ejemplo: si alguno está en servicio -> verde)
      let markerColor = '#007bff';
      if (servicesAtLocation.some(s => s.estado_servicio === 2)) markerColor = '#28a745';
      else if (servicesAtLocation.some(s => s.estado_servicio === 1)) markerColor = '#ffc107';
      else if (servicesAtLocation.some(s => s.estado_servicio === 3)) markerColor = '#dc3545';
    
      const marker = L.circleMarker([lat, lng], {
        radius: 10,
        fillColor: markerColor,
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9,
        className: 'service-marker'
      });
    
      // Popup con todos los servicios de esa ubicación
      let popupContent = `
  <div class="service-popup" style="min-width: 220px; max-height: 200px; overflow-y: auto;">
    <h4>Servicios (${servicesAtLocation.length})</h4>
    <div style="padding-left:5px;margin:0;">
`;
    
      servicesAtLocation.forEach(service => {
        popupContent += `
          <li style="margin-bottom:8px;">
            <strong>${service.nombre_cliente || 'Cliente'} </strong><br/>
            Orden: ${service.orden_servicio_new || 'N/A'}<br/>
            Tel: ${formatPhone(service.telefono)}<br/>
            Estado: <span class="badge ${
              service.estado_servicio === 2 ? 'bg-success' : 
              service.estado_servicio === 1 ? 'bg-warning' : 
              'bg-secondary'
            }">
              ${getStatusText(service.estado_servicio)}
            </span><br/>
            Dirección: ${service.direccion_inicial || 'Sin dirección'}<br/>
            ${service.vehiculo_placa ? `Placa: ${service.vehiculo_placa}` : ''}
          </li>
        `;
      });
    
      popupContent += `</div></div>`;
    
      marker.bindPopup(popupContent);
      markers.addLayer(marker);
      bounds.push([lat, lng]);
    });
    
    
    // Clear existing markers
    mapInstance.eachLayer(layer => {
      if (layer instanceof L.CircleMarker || layer instanceof L.FeatureGroup) {
        mapInstance.removeLayer(layer);
      }
    });
    
    console.log(`Adding ${bounds.length} markers to the map`);
    
    // Add markers to map
    markers.addTo(mapInstance);
    
    // Fit map to bounds if we have valid locations
    if (bounds.length > 0) {
      try {
        // Filter out any invalid coordinates
        const validBounds = bounds.filter(coord => 
          !isNaN(coord[0]) && !isNaN(coord[1]) && 
          coord[0] !== 0 && coord[1] !== 0
        );
        
        if (validBounds.length === 0) {
          console.warn('No valid coordinates found in bounds');
          return;
        }
        
        console.log('Valid bounds to fit:', validBounds);
        
        // Create bounds from valid coordinates
        const boundsObj = L.latLngBounds(validBounds);
        
        // Add a slight offset to prevent markers from being at the very edge
        const padding = [20, 20];
        
        // Fit bounds with error handling
        try {
          mapInstance.fitBounds(boundsObj, { 
            padding: padding,
            maxZoom: 15,
            animate: true
          });
          
          // If all markers are at the same location, zoom in
          if (validBounds.length > 1 && validBounds.every(coord => 
            coord[0] === validBounds[0][0] && coord[1] === validBounds[0][1]
          )) {
            console.log('All markers are at the same location, zooming in');
            mapInstance.setView([validBounds[0][0], validBounds[0][1]], 15);
          }
        } catch (e) {
          console.error('Error fitting bounds:', e);
          // Fallback to default view if bounds fitting fails
          mapInstance.setView([4.7109, -74.0721], 8);
        }
      } catch (e) {
        console.error('Error processing bounds:', e);
        mapInstance.setView([4.7109, -74.0721], 8);
      }
    } else {
      console.warn('No valid bounds to fit the map to');
      mapInstance.setView([4.7109, -74.0721], 8);
    }
  }, [mapadata, mapInstance, mapRef]); // Dependencies array

  return (
    <div className="position-relative">
      <div 
        ref={mapRef}
        style={{ 
          height: '600px', 
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
        className="border"
      >
        {serviceLocations.length > 0 && (
          <div className="position-absolute top-0 end-0 m-3 z-1000">
            <div className="bg-white p-2 rounded shadow-sm">
              <div className="d-flex align-items-center mb-1">
                <span className="badge bg-success me-2" style={{width: '12px', height: '12px', padding: 0}}></span>
                <small>En Servicio</small>
              </div>
              <div className="d-flex align-items-center mb-1">
                <span className="badge bg-warning me-2" style={{width: '12px', height: '12px', padding: 0}}></span>
                <small>Asignado</small>
              </div>
              <div className="d-flex align-items-center">
                <span className="badge bg-danger me-2" style={{width: '12px', height: '12px', padding: 0}}></span>
                <small>Cancelado</small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveServicesMap;
